import React from 'react';

/**
 * A small Markdown renderer for agendas and minutes.
 *
 * Deliberately dependency-free and output-safe: it builds React elements
 * directly, never `dangerouslySetInnerHTML`, so committee-authored text can't
 * inject markup. Supports headings, bold/italic/code, links, bullet and
 * numbered lists, blockquotes, fenced code, and horizontal rules.
 */

// Only http(s) and same-site links — blocks javascript: and data: URLs.
function safeHref(url) {
  const trimmed = String(url).trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith('/') || trimmed.startsWith('#')) return trimmed;
  return null;
}

const INLINE_PATTERN = new RegExp(
  [
    '\\*\\*(.+?)\\*\\*',          // **bold** (lazy, so it may contain *italic*)
    '`([^`]+)`',                  // `code`
    '\\[([^\\]]+)\\]\\(([^)\\s]+)\\)', // [label](url)
    '\\*([^*]+)\\*',              // *italic*
    '_([^_]+)_',                  // _italic_
  ].join('|'),
  'g'
);

function renderInline(text, keyPrefix) {
  const nodes = [];
  let lastIndex = 0;
  let match;
  let i = 0;

  // A fresh regex per call: the pattern is stateful (/g keeps lastIndex), and
  // this function recurses for nested markup like a link inside bold.
  const pattern = new RegExp(INLINE_PATTERN.source, 'g');
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));
    const key = `${keyPrefix}-i${i++}`;

    const [, bold, code, linkLabel, linkUrl, italicStar, italicUnderscore] = match;
    if (bold !== undefined) {
      nodes.push(<strong key={key}>{renderInline(bold, `${key}s`)}</strong>);
    } else if (code !== undefined) {
      nodes.push(<code key={key}>{code}</code>);
    } else if (linkLabel !== undefined) {
      const href = safeHref(linkUrl);
      const label = renderInline(linkLabel, `${key}a`);
      nodes.push(
        href
          ? <a key={key} href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer">{label}</a>
          : <span key={key}>{label}</span>
      );
    } else {
      const inner = italicStar !== undefined ? italicStar : italicUnderscore;
      nodes.push(<em key={key}>{renderInline(inner, `${key}e`)}</em>);
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

function parseBlocks(source) {
  const lines = String(source).replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) { i++; continue; }

    // Fenced code
    if (/^```/.test(line)) {
      const code = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i])) code.push(lines[i++]);
      i++; // closing fence
      blocks.push({ type: 'code', text: code.join('\n') });
      continue;
    }

    // Horizontal rule
    if (/^\s*([-*_])\s*\1\s*\1[\s\-*_]*$/.test(line)) {
      blocks.push({ type: 'hr' });
      i++;
      continue;
    }

    // Heading
    const heading = line.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      blocks.push({ type: 'heading', level: heading[1].length, text: heading[2] });
      i++;
      continue;
    }

    // Blockquote
    if (/^\s*>\s?/.test(line)) {
      const quoted = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
        quoted.push(lines[i].replace(/^\s*>\s?/, ''));
        i++;
      }
      blocks.push({ type: 'quote', text: quoted.join(' ') });
      continue;
    }

    // Lists — a bullet list and a numbered list are the same shape
    const bullet = /^\s*[-*+]\s+(.*)$/;
    const numbered = /^\s*\d+[.)]\s+(.*)$/;
    if (bullet.test(line) || numbered.test(line)) {
      const ordered = numbered.test(line);
      const pattern = ordered ? numbered : bullet;
      const items = [];
      while (i < lines.length && pattern.test(lines[i])) {
        items.push(lines[i].match(pattern)[1]);
        i++;
      }
      blocks.push({ type: 'list', ordered, items });
      continue;
    }

    // Paragraph — runs until a blank line or the start of another block
    const para = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^(#{1,6}\s|```|\s*>|\s*[-*+]\s|\s*\d+[.)]\s)/.test(lines[i])
    ) {
      para.push(lines[i]);
      i++;
    }
    if (para.length) blocks.push({ type: 'paragraph', text: para.join('\n') });
    else i++; // safety valve — never spin on a line we can't classify
  }

  return blocks;
}

export default function Markdown({ children, className = '' }) {
  const source = children || '';
  if (!String(source).trim()) return null;

  const blocks = parseBlocks(source);

  return (
    <div className={`markdown ${className}`.trim()}>
      {blocks.map((block, idx) => {
        const key = `b${idx}`;
        switch (block.type) {
          case 'heading': {
            const Tag = `h${Math.min(block.level + 1, 6)}`; // page owns h1
            return <Tag key={key}>{renderInline(block.text, key)}</Tag>;
          }
          case 'list': {
            const Tag = block.ordered ? 'ol' : 'ul';
            return (
              <Tag key={key}>
                {block.items.map((item, n) => (
                  <li key={`${key}-${n}`}>{renderInline(item, `${key}-${n}`)}</li>
                ))}
              </Tag>
            );
          }
          case 'quote':
            return <blockquote key={key}>{renderInline(block.text, key)}</blockquote>;
          case 'code':
            return <pre key={key}><code>{block.text}</code></pre>;
          case 'hr':
            return <hr key={key} />;
          default:
            return (
              <p key={key}>
                {block.text.split('\n').map((line, n) => (
                  <React.Fragment key={`${key}-l${n}`}>
                    {n > 0 && <br />}
                    {renderInline(line, `${key}-l${n}`)}
                  </React.Fragment>
                ))}
              </p>
            );
        }
      })}
    </div>
  );
}
